import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { TID } from '../lib/testIds';
import { useContactModal, PACKAGE_TO_TYPE } from './ContactModalContext';

const PACKAGES = [
  {
    key: 'brand',
    label: 'Brand Content',
    code: '01',
    price: 'From $2,400 AUD',
    duration: '1–3 day shoots',
    blurb:
      'Premium brand films, social-first cutdowns and product stories built around your campaign.',
    deliverables: ['Hero brand film (60–90s)', '4–6 social cutdowns', 'Studio + location', 'Colour grade', 'Licensed music', 'Stills selects'],
    image:
      'https://images.unsplash.com/photo-1611784728558-6c7d9b409cdf?fm=jpg&q=85&w=1400&auto=format&fit=crop',
  },
  {
    key: 'weddings',
    label: 'Weddings',
    code: '02',
    price: 'From $3,800 AUD',
    duration: 'Full-day coverage',
    blurb:
      'Cinematic wedding films shot like a feature — quiet, real and unmistakably yours, forever.',
    deliverables: ['Highlight film (5–7m)', 'Full ceremony edit', 'Cinematic teaser reel', 'Two shooters', 'Drone coverage', 'Online delivery'],
    image:
      'https://images.unsplash.com/photo-1506355639690-a1f2a100689e?fm=jpg&q=85&w=1400&auto=format&fit=crop',
  },
  {
    key: 'sports',
    label: 'Sports & Athletes',
    code: '03',
    price: 'From $1,800 AUD',
    duration: 'Half / full day',
    blurb:
      'Performance-led films for athletes, teams and combat sports — built to recruit and inspire.',
    deliverables: ['Long-form film (90–120s)', '8+ social verticals', 'Training BTS', 'Trailer cut', 'Slow-motion grabs'],
    image:
      'https://images.pexels.com/photos/31847340/pexels-photo-31847340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400',
  },
  {
    key: 'events',
    label: 'Events & Music',
    code: '04',
    price: 'From $1,500 AUD',
    duration: 'Per event',
    blurb:
      'Live events, launches and music sessions captured with multi-cam coverage and audio sync.',
    deliverables: ['Recap film', 'Live highlights', 'Multi-cam + audio mix', 'Speaker / set grabs'],
    image:
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
  },
  {
    key: 'business',
    label: 'Business',
    code: '05',
    price: 'From $550 AUD / mo',
    duration: 'Monthly subscription',
    blurb:
      'An ongoing content partner for small businesses — a steady stream of social-ready video, every month.',
    deliverables: ['Monthly content shoot', '8–12 social clips', 'Reels + shorts edits', 'Content strategy session', 'Priority scheduling', 'Cancel anytime'],
    image:
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?fm=jpg&q=85&w=1400&auto=format&fit=crop',
  },
  {
    key: 'realestate',
    label: 'Real Estate',
    code: '06',
    price: 'From $349 AUD',
    duration: 'Per property',
    blurb:
      'Drone-led property media that sells listings faster — aerial video, walkthroughs and HDR photography.',
    deliverables: ['Drone aerial video', 'Cinematic walkthrough', '20+ HDR photos', 'Twilight exteriors', 'Floor-plan add-on', '48-hour turnaround'],
    image:
      'https://images.unsplash.com/photo-1579678929710-862f8e01c0b2?fm=jpg&q=85&w=1400&auto=format&fit=crop',
  },
];

function PackageCard({ pkg, i }) {
  const { open } = useContactModal();
  const openModal = () => open(PACKAGE_TO_TYPE[pkg.key] || 'other');
  return (
    <motion.article
      data-testid={TID.packageCard(pkg.key)}
      role="button"
      tabIndex={0}
      onClick={openModal}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal();
        }
      }}
      initial={{ y: 50, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -12, scale: 1.045 }}
      className="group relative isolate overflow-hidden rounded-2xl bg-[#0b0b0b] border border-black/10 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.45)] transition-shadow duration-500 hover:shadow-[0_0_0_1.5px_#D8FF3A,0_30px_70px_-24px_rgba(0,0,0,0.7)] hover:z-10 cursor-pointer focus:outline-none focus-visible:shadow-[0_0_0_2px_#D8FF3A]"
    >
      {/* Hover image */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <img
          src={pkg.image}
          alt=""
          className="h-full w-full object-cover scale-110 group-hover:scale-100 transition-transform duration-[1600ms] ease-out"
        />
        <div className="absolute inset-0 bg-black/72" />
      </div>

      <div className="relative z-10 p-6 md:p-7 min-h-[460px] md:min-h-[500px] flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between mb-8">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow">
              {pkg.code}
            </span>
            <ArrowUpRight
              size={18}
              className="text-white/40 group-hover:text-[#D8FF3A] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
            />
          </div>

          <h3
            className="font-display text-white font-bold tracking-tight mb-4"
            style={{ fontSize: 'clamp(24px, 2vw, 32px)', lineHeight: 1 }}
          >
            {pkg.label}
          </h3>

          <p className="text-sm text-white/60 leading-relaxed mb-6">
            {pkg.blurb}
          </p>

          <p className="text-[9px] uppercase tracking-[0.3em] text-white/35 mb-3">Included</p>
          <ul className="space-y-1.5 mb-2">
            {pkg.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-2 text-xs text-white/70">
                <span className="mt-1.5 h-1 w-1 shrink-0 bg-[#D8FF3A]" aria-hidden />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="hairline mb-5" />
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1">
                Starting
              </p>
              <p className="font-display text-xl font-bold text-white">{pkg.price}</p>
              <p className="text-[11px] text-white/50 mt-1">{pkg.duration}</p>
            </div>
            <button
              type="button"
              data-testid={TID.packageCta(pkg.key)}
              onClick={(e) => {
                e.stopPropagation();
                openModal();
              }}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#D8FF3A] border-b border-[#D8FF3A]/40 pb-1 hover:border-[#D8FF3A] hover:neon-text-glow transition-all"
            >
              Enquire <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Packages() {
  return (
    <section
      id="packages"
      data-testid={TID.packagesRoot}
      className="relative bg-[#EFE9DC] text-[#0a0a0a] border-y border-black/10"
    >
      <div className="px-6 md:px-14 pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="flex flex-wrap items-end justify-between gap-8 max-w-7xl">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#0a0a0a]/70 mb-6 inline-flex items-center gap-3">
              ◆ Services · 06
              <span className="inline-block h-px w-10 bg-[#0a0a0a]/40" />
              <span className="inline-block h-1.5 w-1.5 bg-[#D8FF3A]" />
            </p>
            <motion.h2
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-[#0a0a0a] font-bold tracking-tighter leading-[1] md:leading-[0.9] text-balance"
              style={{ fontSize: 'clamp(40px, 8vw, 140px)' }}
            >
              Six ways<br />
              <span className="text-[#0a0a0a]/35">we work.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="max-w-md text-[#0a0a0a]/75 text-base md:text-lg leading-relaxed"
          >
            Every project is shaped to fit you — from a single-day brand shoot to multi-day
            documentary builds. Below is where most clients start.
          </motion.p>
        </div>
      </div>

      <div className="px-6 md:px-14 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {PACKAGES.map((p, i) => (
            <PackageCard key={p.key} pkg={p} i={i} />
          ))}
        </div>
      </div>

      <div className="px-6 md:px-14 py-10 md:py-14 mt-10 border-t border-[#0a0a0a]/15 flex flex-wrap items-center justify-between gap-6">
        <p className="text-sm md:text-base text-[#0a0a0a]/70 max-w-xl">
          Need something custom — a brand campaign, a doc series, an ongoing retainer? We build
          bespoke production packages too.
        </p>
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="group inline-flex items-center gap-3 bg-[#0a0a0a] text-white px-6 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#D8FF3A] hover:text-black hover:neon-glow transition-all"
        >
          Tailor a package <span aria-hidden className="group-hover:translate-x-1 transition-transform">→</span>
        </a>
      </div>
    </section>
  );
}
